import type { TerminalNode } from "antlr4ng";

import type { Expressão } from "../nodes/index.js";

/**
 * O valor de cada membro é a palavra-chave do Portugol e também o `getNome()` do `TipoDado`
 * do Portugol Studio, que é o que aparece nas mensagens.
 */
export enum TipoPrimitivo {
  INTEIRO = "inteiro",
  REAL = "real",
  CADEIA = "cadeia",
  LÓGICO = "logico",
  VAZIO = "vazio",
  CARACTER = "caracter",
}

/**
 * O sufixo de cada dimensão num tipo escrito: `inteiro[]`, `real[][]`.
 */
export const SUFIXO_DA_DIMENSÃO = { vetor: "[]", matriz: "[][]" } as const;

export type Tipo = { primitivo: TipoPrimitivo } & (
  | { dimensão?: undefined }
  | { dimensão: "vetor"; tamanho?: Expressão }
  | { dimensão: "matriz"; linhas?: Expressão; colunas?: Expressão }
);

/**
 * Um tipo como se escreve no código: `inteiro`, `real[]`, `cadeia[][]`.
 */
export function tipoNoCódigo(primitivo: string, dimensão?: keyof typeof SUFIXO_DA_DIMENSÃO) {
  return dimensão ? primitivo + SUFIXO_DA_DIMENSÃO[dimensão] : primitivo;
}

/**
 * O curinga `todos` só existe em assinaturas de biblioteca (ver `bibliotecas/`) e casa com
 * qualquer tipo; nenhum programa pode declará-lo.
 */
export const TIPO_TODOS = "todos";

export type TipoOperando = TipoPrimitivo | typeof TIPO_TODOS;

/**
 * As mensagens que interpolam o `TipoDado` direto usam o `toString()` do Java, que devolve
 * a *descrição*: só `logico` diverge do valor do enum.
 */
export function descreverTipo(tipo: TipoOperando): string {
  return tipo === TipoPrimitivo.LÓGICO ? "lógico" : tipo;
}

export function parseTipoPrimitivo(tipo: TerminalNode | null): TipoPrimitivo {
  if (!tipo) {
    return TipoPrimitivo.VAZIO;
  }

  const text = tipo.getText();

  switch (text) {
    case "inteiro": {
      return TipoPrimitivo.INTEIRO;
    }

    case "real": {
      return TipoPrimitivo.REAL;
    }

    case "cadeia": {
      return TipoPrimitivo.CADEIA;
    }

    case "logico": {
      return TipoPrimitivo.LÓGICO;
    }

    case "vazio": {
      return TipoPrimitivo.VAZIO;
    }

    case "caracter": {
      return TipoPrimitivo.CARACTER;
    }

    default: {
      throw new Error(`Tipo desconhecido: ${text}`);
    }
  }
}
